/**
 * Workflow Types
 *
 * Types for the Job Workflow Wizard (Phase 8).
 * TurboTax-style guided workflow that walks users through permit preparation.
 */

import type { ConfidenceLevel, ContractorRole } from './wizard';

// ============================================================================
// WORKFLOW STEP IDENTIFIERS
// ============================================================================

/**
 * Workflow step identifiers for the job workflow wizard
 * Different from WizardStep which is for initial job creation
 */
export type WorkflowStepId =
  | 'basics'      // Step 1: Job snapshot & jurisdiction context
  | 'scope'       // Step 2: Scope & permit type confirmation
  | 'fields'      // Step 3: Required application fields
  | 'documents'   // Step 4: Document upload & verification
  | 'compliance'  // Step 5: Compliance preflight check
  | 'inspections' // Step 6: Inspection requirements
  | 'review';     // Step 7: Review & submit readiness

/**
 * Status for each workflow step
 */
export type WorkflowStepStatus =
  | 'pending'     // Not started (gray outline ○)
  | 'in_progress' // Currently working on
  | 'review'      // Needs user confirmation (amber diamond ◆)
  | 'ready'       // Complete (green solid ■)
  | 'blocked';    // Cannot proceed (red circle ●)

// ============================================================================
// WORKFLOW STEP CONFIGURATION
// ============================================================================

export interface WorkflowSubstep {
  id: string;
  title: string;
  description?: string;
  required: boolean;
  completionField?: string; // Field path to check for completion
}

export interface WorkflowStepConfig {
  id: WorkflowStepId;
  number: number;
  title: string;
  description: string;
  shortTitle: string; // For narrow sidebar
  substeps: WorkflowSubstep[];
  helpTopics?: string[];
}

/**
 * All workflow steps configuration
 */
export const WORKFLOW_STEPS: WorkflowStepConfig[] = [
  {
    id: 'basics',
    number: 1,
    title: 'Basics & Job Snapshot',
    shortTitle: 'Basics',
    description: 'Confirm job identity & jurisdiction context',
    substeps: [
      { id: 'address', title: 'Address confirmation', required: true, completionField: 'address' },
      { id: 'jurisdiction', title: 'Jurisdiction confirmation', required: true, completionField: 'jurisdictionId' },
      { id: 'propertyType', title: 'Property type', required: true, completionField: 'propertyType' },
      { id: 'valuation', title: 'Project valuation', required: false, completionField: 'estimatedValue' },
      { id: 'contractorRole', title: 'Contractor role', required: true, completionField: 'contractorRole' },
    ],
    helpTopics: ['jurisdiction_lookup', 'property_types', 'contractor_roles'],
  },
  {
    id: 'scope',
    number: 2,
    title: 'Scope & Permit Definition',
    shortTitle: 'Scope',
    description: 'Confirm scope + permit type recommendation',
    substeps: [
      { id: 'trade', title: 'Trade confirmation', required: true, completionField: 'permitType' },
      { id: 'jobScope', title: 'Job scope selection', required: true, completionField: 'jobScopeId' },
      { id: 'permitType', title: 'Permit type confirmation', required: true, completionField: 'permitTypeId' },
      { id: 'override', title: 'Override handling', required: false },
    ],
    helpTopics: ['permit_types', 'scope_selection', 'common_scope_mistakes'],
  },
  {
    id: 'fields',
    number: 3,
    title: 'Required Fields',
    shortTitle: 'Data',
    description: 'Complete permit application data',
    substeps: [
      { id: 'applicant', title: 'Applicant info', required: true },
      { id: 'propertyOwner', title: 'Property owner', required: false },
      { id: 'projectDescription', title: 'Project description', required: true },
      { id: 'tradeSpecific', title: 'Trade-specific fields', required: true },
      { id: 'unknownHandling', title: '"I don\'t know" handling', required: false },
    ],
    helpTopics: ['field_requirements', 'common_rejection_reasons'],
  },
  {
    id: 'documents',
    number: 4,
    title: 'Documents / Packet Builder',
    shortTitle: 'Docs',
    description: 'Upload required documents and verify them',
    substeps: [
      { id: 'requiredDocs', title: 'Required documents list', required: true },
      { id: 'perDocument', title: 'Per-document card', required: true },
      { id: 'verification', title: 'Document verification', required: false },
      { id: 'missingDocs', title: 'Missing documents action', required: false },
    ],
    helpTopics: ['document_requirements', 'document_verification'],
  },
  {
    id: 'compliance',
    number: 5,
    title: 'Compliance Preflight',
    shortTitle: 'Comply',
    description: 'Run checks & resolve blockers',
    substeps: [
      { id: 'documentChecks', title: 'Document checks', required: true },
      { id: 'fieldChecks', title: 'Field checks', required: true },
      { id: 'jurisdictionChecks', title: 'Jurisdiction requirements', required: true },
      { id: 'licenseChecks', title: 'License verification', required: true },
      { id: 'tradeChecks', title: 'Trade-specific requirements', required: true },
    ],
    helpTopics: ['compliance_requirements', 'blocker_resolution'],
  },
  {
    id: 'inspections',
    number: 6,
    title: 'Inspections & Checkpoints',
    shortTitle: 'Inspect',
    description: 'Configure inspection requirements',
    substeps: [
      { id: 'requiredInspections', title: 'Required inspections', required: true },
      { id: 'checkpoints', title: 'Checkpoint checklist', required: false },
      { id: 'knownTips', title: 'Known tips', required: false },
      { id: 'acknowledgment', title: 'Acknowledgment', required: false },
    ],
    helpTopics: ['inspection_types', 'inspection_tips'],
  },
  {
    id: 'review',
    number: 7,
    title: 'Review & Submit',
    shortTitle: 'Review',
    description: 'Final review & submission readiness',
    substeps: [
      { id: 'summary', title: 'Job summary', required: true },
      { id: 'checklist', title: 'Completion checklist', required: true },
      { id: 'warnings', title: 'Remaining warnings', required: false },
      { id: 'packetPreview', title: 'Packet preview', required: true },
    ],
    helpTopics: ['submission_process', 'portal_guidance'],
  },
];

// ============================================================================
// WORKFLOW ISSUES
// ============================================================================

/**
 * Issue severity levels (triaged)
 */
export type IssueSeverity =
  | 'blocker'  // Red - Must fix before submission
  | 'warning'  // Amber - Should review before submission
  | 'info';    // Blue - FYI items

/**
 * Issue category for grouping
 */
export type IssueCategory =
  | 'document'     // Missing/invalid documents
  | 'field'        // Missing/invalid fields
  | 'jurisdiction' // Jurisdiction-specific requirements
  | 'license'      // License scope issues
  | 'trade';       // Trade-specific code requirements

/**
 * A single issue in the workflow
 */
export interface WorkflowIssue {
  id: string;
  severity: IssueSeverity;
  category: IssueCategory;
  message: string;
  details?: string;
  fixStepId: WorkflowStepId;
  fixSubstepId?: string;
  fixAction?: string; // CTA label like "Upload Document" or "Complete Field"
  source?: string;    // Where the issue came from (e.g., "compliance_check", "validation")
  createdAt?: string;
}

// ============================================================================
// WORKFLOW STATE
// ============================================================================

/**
 * Progress metrics for the workflow
 */
export interface WorkflowProgress {
  dataCompletenessPct: number;      // Fields filled / total required
  packetCompletenessPct: number;    // Docs uploaded / total required
  complianceReadinessPct: number;   // Checks passing / total checks
}

/**
 * Complete workflow state for a job
 * Stored in Firestore under job.workflow
 */
export interface JobWorkflowState {
  jobId: string;

  // Navigation
  currentStepId: WorkflowStepId;
  currentSubstepId?: string;

  // Step status tracking
  stepStatus: Record<WorkflowStepId, WorkflowStepStatus>;

  // Completion substeps per step
  completedSubsteps: Record<WorkflowStepId, string[]>;

  // Progress metrics (computed)
  progress: WorkflowProgress;

  // Issue registry (populated by compliance check)
  blockers: WorkflowIssue[];
  warnings: WorkflowIssue[];
  info: WorkflowIssue[];

  // Audit
  lastUpdatedAt: string;
  lastUpdatedBy: string;
  lastComplianceCheckAt?: string;
}

/**
 * Initial state factory
 */
export function createInitialWorkflowState(jobId: string, userId: string): JobWorkflowState {
  return {
    jobId,
    currentStepId: 'basics',
    currentSubstepId: 'address',
    stepStatus: {
      basics: 'pending',
      scope: 'pending',
      fields: 'pending',
      documents: 'pending',
      compliance: 'pending',
      inspections: 'pending',
      review: 'pending',
    },
    completedSubsteps: {
      basics: [],
      scope: [],
      fields: [],
      documents: [],
      compliance: [],
      inspections: [],
      review: [],
    },
    progress: {
      dataCompletenessPct: 0,
      packetCompletenessPct: 0,
      complianceReadinessPct: 0,
    },
    blockers: [],
    warnings: [],
    info: [],
    lastUpdatedAt: new Date().toISOString(),
    lastUpdatedBy: userId,
  };
}

// ============================================================================
// FIELD CONFIDENCE
// ============================================================================

/**
 * A field value with confidence tracking
 */
export interface ConfidentFieldValue<T = string> {
  value: T;
  confidence: ConfidenceLevel;
  source: 'job' | 'contractor' | 'jurisdiction' | 'generated' | 'default' | 'user_override';
  userOverridden?: boolean;
  originalValue?: T;
}

/**
 * Field definition for dynamic form generation
 */
export interface WorkflowField {
  id: string;
  name: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'date' | 'textarea' | 'checkbox' | 'radio';
  required: boolean;
  placeholder?: string;
  helpText?: string;
  options?: Array<{ value: string; label: string }>;
  validation?: {
    pattern?: string;
    min?: number;
    max?: number;
    minLength?: number;
    maxLength?: number;
  };
  defaultValue?: string;
  sourceField?: string; // Field path to auto-fill from
  tradeSpecific?: string[]; // Only show for these trades
}

// ============================================================================
// DOCUMENT STATUS
// ============================================================================

/**
 * Document status in the workflow
 */
export type DocumentWorkflowStatus =
  | 'missing'   // Red - Required but not uploaded
  | 'uploaded'  // Amber - Uploaded but not verified
  | 'verified'  // Green - Processed and validated
  | 'optional'; // Gray - Nice to have, not required

/**
 * A document requirement in the workflow
 */
export interface WorkflowDocument {
  id: string;
  categoryId?: number;
  name: string;
  description?: string;
  required: boolean;
  status: DocumentWorkflowStatus;
  uploadedAt?: string;
  uploadedBy?: string;
  verifiedAt?: string;
  extractedFields?: Record<string, string>;
  documentId?: string; // Reference to actual document in job_documents
}

// ============================================================================
// SUBMISSION STATE
// ============================================================================

/**
 * Submission tracking
 */
export interface WorkflowSubmission {
  status: 'not_ready' | 'ready' | 'submitted' | 'approved' | 'rejected';
  submittedAt?: string;
  confirmationNumber?: string;
  portalReference?: string;
  notes?: string;
  rejectionReason?: string;
}

// ============================================================================
// HELPER TYPES
// ============================================================================

/**
 * Get step by ID
 */
export function getWorkflowStep(stepId: WorkflowStepId): WorkflowStepConfig | undefined {
  return WORKFLOW_STEPS.find(s => s.id === stepId);
}

/**
 * Get next step
 */
export function getNextWorkflowStep(currentStepId: WorkflowStepId): WorkflowStepConfig | undefined {
  const currentIndex = WORKFLOW_STEPS.findIndex(s => s.id === currentStepId);
  if (currentIndex < 0 || currentIndex >= WORKFLOW_STEPS.length - 1) return undefined;
  return WORKFLOW_STEPS[currentIndex + 1];
}

/**
 * Get previous step
 */
export function getPreviousWorkflowStep(currentStepId: WorkflowStepId): WorkflowStepConfig | undefined {
  const currentIndex = WORKFLOW_STEPS.findIndex(s => s.id === currentStepId);
  if (currentIndex <= 0) return undefined;
  return WORKFLOW_STEPS[currentIndex - 1];
}

/**
 * Check if workflow has any blockers
 */
export function hasBlockers(state: JobWorkflowState): boolean {
  return state.blockers.length > 0;
}

/**
 * Check if workflow is ready for submission
 */
export function isReadyForSubmission(state: JobWorkflowState): boolean {
  return (
    state.blockers.length === 0 &&
    state.progress.dataCompletenessPct >= 100 &&
    state.progress.packetCompletenessPct >= 100
  );
}

/**
 * Get issues for a specific step
 */
export function getIssuesForStep(
  state: JobWorkflowState,
  stepId: WorkflowStepId
): { blockers: WorkflowIssue[]; warnings: WorkflowIssue[]; info: WorkflowIssue[] } {
  return {
    blockers: state.blockers.filter(i => i.fixStepId === stepId),
    warnings: state.warnings.filter(i => i.fixStepId === stepId),
    info: state.info.filter(i => i.fixStepId === stepId),
  };
}

/**
 * Calculate step status based on completion and issues
 */
export function calculateStepStatus(
  stepId: WorkflowStepId,
  completedSubsteps: string[],
  issues: { blockers: WorkflowIssue[]; warnings: WorkflowIssue[] }
): WorkflowStepStatus {
  const step = getWorkflowStep(stepId);
  if (!step) return 'pending';

  const requiredSubsteps = step.substeps.filter(s => s.required);
  const allRequiredComplete = requiredSubsteps.every(s => completedSubsteps.includes(s.id));

  if (issues.blockers.length > 0) return 'blocked';
  if (!allRequiredComplete) return 'pending';
  if (issues.warnings.length > 0) return 'review';
  return 'ready';
}

// ============================================================================
// COMPLIANCE FINDING MAPPING
// ============================================================================

/**
 * Compliance finding from useCompliance hook
 * (Minimal interface to avoid circular imports)
 */
interface ComplianceFinding {
  ruleCode: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  description: string;
  affectedField?: string;
  suggestedFix?: string;
  status: string;
}

/**
 * Map affectedField or ruleCode to a workflow step
 */
function mapFindingToStep(finding: ComplianceFinding): { stepId: WorkflowStepId; substepId?: string } {
  const { affectedField, ruleCode } = finding;
  const field = affectedField?.toLowerCase() || '';
  const code = ruleCode?.toLowerCase() || '';

  // Document-related findings → documents step
  if (
    field.includes('document') ||
    code.includes('doc') ||
    code.includes('missing_doc') ||
    code.includes('upload')
  ) {
    return { stepId: 'documents', substepId: 'requiredDocs' };
  }

  // License-related findings → basics or compliance step
  if (
    field.includes('license') ||
    code.includes('license') ||
    code.includes('lic_')
  ) {
    return { stepId: 'compliance', substepId: 'licenseChecks' };
  }

  // Trade-specific findings → scope or compliance step
  if (
    code.includes('trade_') ||
    code.includes('electrical') ||
    code.includes('mechanical') ||
    code.includes('plumbing') ||
    code.includes('roofing')
  ) {
    return { stepId: 'compliance', substepId: 'tradeChecks' };
  }

  // Jurisdiction-specific findings → compliance step
  if (
    code.includes('jurisdiction') ||
    code.includes('hvhz') ||
    code.includes('coastal') ||
    code.includes('flood')
  ) {
    return { stepId: 'compliance', substepId: 'jurisdictionChecks' };
  }

  // Field-related findings → fields step
  if (
    field.includes('applicant') ||
    field.includes('owner') ||
    field.includes('contact') ||
    code.includes('missing_field') ||
    code.includes('field_')
  ) {
    return { stepId: 'fields', substepId: 'applicant' };
  }

  // Address/property findings → basics step
  if (
    field.includes('address') ||
    field.includes('property') ||
    field.includes('valuation') ||
    code.includes('address') ||
    code.includes('property')
  ) {
    return { stepId: 'basics', substepId: 'address' };
  }

  // Scope/permit type findings → scope step
  if (
    field.includes('scope') ||
    field.includes('permit') ||
    code.includes('scope') ||
    code.includes('permit_type')
  ) {
    return { stepId: 'scope', substepId: 'permitType' };
  }

  // Default to compliance step for other findings
  return { stepId: 'compliance', substepId: 'fieldChecks' };
}

/**
 * Determine issue category from finding
 */
function mapFindingToCategory(finding: ComplianceFinding): IssueCategory {
  const { affectedField, ruleCode } = finding;
  const field = affectedField?.toLowerCase() || '';
  const code = ruleCode?.toLowerCase() || '';

  if (field.includes('document') || code.includes('doc')) return 'document';
  if (field.includes('license') || code.includes('license')) return 'license';
  if (code.includes('jurisdiction') || code.includes('hvhz') || code.includes('overlay')) return 'jurisdiction';
  if (code.includes('trade_') || code.includes('electrical') || code.includes('mechanical')) return 'trade';
  return 'field';
}

/**
 * Generate fix action label from finding
 */
function mapFindingToFixAction(finding: ComplianceFinding): string {
  const { affectedField, ruleCode, suggestedFix } = finding;
  const field = affectedField?.toLowerCase() || '';
  const code = ruleCode?.toLowerCase() || '';

  if (field.includes('document') || code.includes('doc')) return 'Upload Document';
  if (field.includes('license') || code.includes('license')) return 'Verify License';
  if (suggestedFix) return suggestedFix.length > 30 ? 'Fix Issue' : suggestedFix;
  return 'Review & Fix';
}

/**
 * Map compliance findings to workflow issues
 * Converts ComplianceCheckResult findings to triaged WorkflowIssue arrays
 */
export function mapComplianceFindingsToIssues(
  findings: ComplianceFinding[]
): { blockers: WorkflowIssue[]; warnings: WorkflowIssue[]; info: WorkflowIssue[] } {
  const result: { blockers: WorkflowIssue[]; warnings: WorkflowIssue[]; info: WorkflowIssue[] } = {
    blockers: [],
    warnings: [],
    info: [],
  };

  findings.forEach((finding, index) => {
    const { stepId, substepId } = mapFindingToStep(finding);

    const issue: WorkflowIssue = {
      id: `compliance-${finding.ruleCode}-${index}`,
      severity: finding.severity === 'critical' ? 'blocker' : finding.severity,
      category: mapFindingToCategory(finding),
      message: finding.title,
      details: finding.description,
      fixStepId: stepId,
      fixSubstepId: substepId,
      fixAction: mapFindingToFixAction(finding),
      source: 'compliance_check',
      createdAt: new Date().toISOString(),
    };

    // Add to appropriate array based on severity
    if (finding.severity === 'critical') {
      result.blockers.push(issue);
    } else if (finding.severity === 'warning') {
      result.warnings.push(issue);
    } else {
      result.info.push(issue);
    }
  });

  return result;
}

/**
 * Merge new issues with existing issues (deduplicating by rule code)
 */
export function mergeWorkflowIssues(
  existing: { blockers: WorkflowIssue[]; warnings: WorkflowIssue[]; info: WorkflowIssue[] },
  incoming: { blockers: WorkflowIssue[]; warnings: WorkflowIssue[]; info: WorkflowIssue[] }
): { blockers: WorkflowIssue[]; warnings: WorkflowIssue[]; info: WorkflowIssue[] } {
  const dedupeById = (issues: WorkflowIssue[]): WorkflowIssue[] => {
    const seen = new Set<string>();
    return issues.filter(issue => {
      if (seen.has(issue.id)) return false;
      seen.add(issue.id);
      return true;
    });
  };

  return {
    blockers: dedupeById([...incoming.blockers, ...existing.blockers]),
    warnings: dedupeById([...incoming.warnings, ...existing.warnings]),
    info: dedupeById([...incoming.info, ...existing.info]),
  };
}
